import axios from 'axios';
import { isPublicAppPath } from './publicPaths';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Create axios instance with correct base URL
const api = axios.create({
  baseURL: `${API_URL}/api`, // backend already has /api prefix
  withCredentials: true,
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("access_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const path = window.location?.pathname || '';
      if (!isPublicAppPath(path)) {
        localStorage.removeItem("access_token");
        localStorage.removeItem("refresh_token");
        // Best-effort cookie clear (httpOnly cookies need server logout)
        axios.post(`${API_URL}/api/auth/logout`, {}, { withCredentials: true }).catch(() => {});
        window.location.href = "/login";
      }
    }
    return Promise.reject(error);
  },
);

// ================= AUTH =================
export const authApi = {
  login: (email, password) => api.post("/auth/login", { email, password }),
  register: (data) => api.post("/auth/register", data),
  registerCompany: (data) => api.post("/auth/register/company", data),
  verifyEmail: (token) => axios.get(`${API_URL}/api/auth/verify-email`, { params: { token } }),
  forgotPassword: (email) => api.post("/auth/forgot-password", { email }),
  resetPassword: (token, password) => api.post("/auth/reset-password", { token, password }),
  logout: () => api.post("/auth/logout"),
  me: () => api.get("/auth/me"),
  publicPlans: (currency = "INR") =>
    axios.get(`${API_URL}/api/billing/public-plans`, { params: { currency } }),
};

export function getBackendOrigin() {
  return API_URL || '';
}

// ================= PRODUCTS =================
export const productsApi = {
  list: () => api.get("/products"),
  get: (id) => api.get(`/products/${id}`),
  create: (data) => api.post("/products", data),
  update: (id, data) => api.put(`/products/${id}`, data),
  delete: (id) => api.delete(`/products/${id}`),
  downloadImportTemplate: () =>
    api.get("/products/import/template", { responseType: "blob" }),
  previewImport: (file, sheetName) => {
    const form = new FormData();
    form.append("file", file);
    return api.post("/products/import/preview", form, {
      params: sheetName ? { sheet_name: sheetName } : undefined,
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  importCatalog: (file, { sheetName, skipDuplicates = true } = {}) => {
    const form = new FormData();
    form.append("file", file);
    return api.post("/products/import", form, {
      params: {
        ...(sheetName ? { sheet_name: sheetName } : {}),
        skip_duplicates: skipDuplicates,
      },
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
};

// ================= QR =================
export const qrApi = {
  generate: (productId, quantity, opts = {}) =>
    api.post("/qr/generate", {
      product_id: productId || undefined,
      quantity,
      generation_mode: opts.generation_mode || (productId ? "linked" : "preprint"),
      preprint_scope: opts.preprint_scope || "intended",
      batch_number: opts.batch_number || undefined,
    }),

  mapMasterProduct: (masterQrId, data) =>
    api.post(`/qr/masters/${encodeURIComponent(masterQrId)}/map-product`, data),

  createSubMasterShipment: (data) => api.post('/qr/submasters/shipment', data),

  listSubMasters: (masterQrId) =>
    api.get(`/qr/masters/${encodeURIComponent(masterQrId)}/submasters`),

  setMasterRegions: (masterQrId, data) =>
    api.put(`/qr/masters/${encodeURIComponent(masterQrId)}/regions`, data),

  list: (params) => api.get("/qr", { params }),
  listMasters: () => api.get("/qr", { params: { is_master: true } }),
  listChildren: (masterQrId) =>
    api.get("/qr", { params: { master_qr_id: masterQrId } }),

  masterSummary: () => api.get("/qr/master-summary"),
  get: (qrId) => api.get(`/qr/${qrId}`),

  scan: (data) => api.post("/qr/scan", data),

  // Public endpoint (bypasses interceptor)
  verify: (qrId, params) =>
    axios.get(`${API_URL}/api/verify/${qrId}`, { params }),

  decodePki: (encryptedPki) =>
    api.post("/qr/decode-pki", { encrypted_pki: encryptedPki }),

  publicKey: () => api.get("/pki/public-key"),

  recordPrint: (qrId) => api.post(`/qr/${qrId}/print`),

  requestReprint: (qrId, reason) =>
    api.post("/qr/reprint-request", { qr_id: qrId, reason }),

  listReprintRequests: (status) =>
    api.get("/qr/reprint-requests", {
      params: status ? { status } : {},
    }),

  reviewReprintRequest: (requestId, approved, adminNotes) =>
    api.post(`/qr/reprint-requests/${requestId}/review`, {
      approved,
      admin_notes: adminNotes,
    }),

  bulkUpdate: (data) => api.post("/qr/bulk-update", data),
  batchPrint: (qrIds) => api.post("/qr/batch-print", qrIds),
  setLifecycle: (qrId, data) => api.post(`/qr/${encodeURIComponent(qrId)}/lifecycle`, data),
  bulkLifecycle: (data) => api.post("/qr/lifecycle/bulk", data),
};

// ================= USERS =================
export const usersApi = {
  list: () => api.get("/users"),
  create: (data) => api.post("/users", data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
};

// ================= ROLES =================
export const rolesApi = {
  list: () => api.get("/roles"),
  create: (data) => api.post("/roles", data),
  update: (id, data) => api.put(`/roles/${id}`, data),
  delete: (id) => api.delete(`/roles/${id}`),
};

// ================= DEPARTMENTS =================
export const departmentsApi = {
  list: () => api.get("/departments"),
  create: (data) => api.post("/departments", data),
  update: (id, data) => api.put(`/departments/${id}`, data),
  delete: (id) => api.delete(`/departments/${id}`),
};

// ================= PERMISSIONS =================
export const permissionsApi = {
  list: () => api.get("/permissions"),
  create: (data) => api.post("/permissions", data),
  delete: (id) => api.delete(`/permissions/${id}`),
};

// ================= ORGANIZATIONS =================
export const organizationsApi = {
  list: () => api.get("/organizations"),
  create: (data) => api.post("/organizations", data),
  update: (id, data) => api.put(`/organizations/${id}`, data),
};

// ================= PROFILE =================
export const profileApi = {
  get: () => api.get("/profile"),
  update: (data) => api.put("/profile", data),
  changePassword: (data) => api.put("/profile/password", data),
};

// ================= AUDIT =================
export const auditApi = {
  list: (params) => api.get("/audit-logs", { params }),
};

// ================= WORKFLOW =================
export const workflowApi = {
  listTemplates: () => api.get("/workflows/templates"),
  getTemplate: (id) => api.get(`/workflows/templates/${id}`),
  createTemplate: (data) => api.post("/workflows/templates", data),
  updateTemplate: (id, data) => api.put(`/workflows/templates/${id}`, data),
  deleteTemplate: (id) => api.delete(`/workflows/templates/${id}`),

  assignToProduct: (productId, templateId) =>
    api.put(`/workflows/products/${productId}/assign`, {
      template_id: templateId,
    }),

  startInstance: (data) => api.post("/workflows/instances/start", data),

  advanceInstance: (instanceId, data) =>
    api.post(`/workflows/instances/${instanceId}/advance`, data),

  getInstance: (id) => api.get(`/workflows/instances/${id}`),

  listInstances: (params) => api.get("/workflows/instances", { params }),
};

// ================= REGIONS =================
export const regionsApi = {
  list: (q) => api.get("/regions", { params: q ? { q } : undefined }),
  create: (data) => api.post("/regions", data),
  update: (id, data) => api.put(`/regions/${id}`, data),
  delete: (id) => api.delete(`/regions/${id}`),
};

// ================= STATS =================
export const statsApi = {
  dashboard: () => api.get("/stats/dashboard"),
  qr: () => api.get("/stats/qr"),
  scans: (params) => api.get("/reports/scans", { params }),
  counterfeit: () => api.get("/stats/counterfeit"),
  stolen: () => api.get("/stats/stolen"),
  incidentGeo: (params) => api.get("/stats/incidents/geo", { params }),
};

// ================= PRINT JOBS =================
export const printJobsApi = {
  list: (params) => api.get("/print-jobs", { params }),
  get: (jobId) => api.get(`/print-jobs/${encodeURIComponent(jobId)}`),
  create: (data) => api.post("/print-jobs", data),
  localPrint: (jobId) => api.post(`/print-jobs/${encodeURIComponent(jobId)}/local-print`),
  cancel: (jobId) => api.post(`/print-jobs/${encodeURIComponent(jobId)}/cancel`),
};

// ================= CONTRACT PRINTERS =================
export const contractPrintersApi = {
  list: () => api.get("/contract-printers"),
  create: (data) => api.post("/contract-printers", data),
  update: (id, data) => api.put(`/contract-printers/${id}`, data),
  resendEmail: (id) => api.post(`/contract-printers/${id}/send-registration-email`),
};

// ================= LABEL PRINTERS =================
export const labelPrintersApi = {
  list: () => api.get("/label-printers"),
  create: (data) => api.post("/label-printers", data),
  update: (id, data) => api.put(`/label-printers/${id}`, data),
  remove: (id) => api.delete(`/label-printers/${id}`),
};

// ================= CONSUMER (public product / warranty forms) =================
export const consumerApi = {
  context: (qrId) => api.get(`/consumer/qr/${encodeURIComponent(qrId)}/context`),
  registerProduct: (qrId, data) =>
    api.post(`/consumer/qr/${encodeURIComponent(qrId)}/register-product`, data),
  warrantyClaim: (qrId, data) =>
    api.post(`/consumer/qr/${encodeURIComponent(qrId)}/warranty-claim`, data),
  support: (qrId, data) =>
    api.post(`/consumer/qr/${encodeURIComponent(qrId)}/support`, data),
};

export default api;
